import React from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  TooltipProps,
} from 'recharts';
import { Wheat, Loader2 } from 'lucide-react';

/**
 * Interface for food history data point
 */
export interface FoodHistoryData {
  date: string;
  available: number;
  consumed: number;
}

interface FoodHistoryChartProps {
  data: FoodHistoryData[];
  loading?: boolean;
  error?: string | null;
  height?: number;
}

/**
 * Custom tooltip component for food consumption display
 */
const CustomTooltip: React.FC<TooltipProps<number, string>> = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const available = payload.find(p => p.dataKey === 'available')?.value || 0;
    const consumed = payload.find(p => p.dataKey === 'consumed')?.value || 0;
    const remaining = (available as number) - (consumed as number);

    return (
      <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
        <p className="font-semibold text-gray-700 mb-2">{label}</p>
        <div className="space-y-1">
          <p className="text-amber-600">
            Available: <span className="font-bold">{available} kg</span>
          </p>
          <p className="text-yellow-600">
            Consumed: <span className="font-bold">{consumed} kg</span>
          </p>
          <p className={`text-sm font-semibold ${remaining > 0 ? 'text-green-600' : 'text-red-600'}`}>
            Remaining: {remaining} kg
          </p>
        </div>
      </div>
    );
  }
  return null;
};

/**
 * FoodHistoryChart - Area/Stacked chart showing feed consumption vs availability
 *
 * Features:
 * - Displays available feed vs consumed feed over time
 * - Amber/yellow color scheme for agricultural theme
 * - Stacked area visualization for easy comparison
 * - Interactive tooltips with remaining feed calculation
 * - Responsive design for all devices
 * - Loading and error states
 *
 * @param data - Array of daily food availability and consumption data
 * @param loading - Loading state indicator
 * @param error - Error message if data fetch failed
 * @param height - Chart height in pixels (default: 350)
 */
const FoodHistoryChart: React.FC<FoodHistoryChartProps> = ({
  data,
  loading = false,
  error = null,
  height = 350,
}) => {
  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
        <span className="ml-2 text-gray-600">Loading feed data...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <div className="text-center">
          <p className="text-red-600 font-semibold">Failed to load chart</p>
          <p className="text-sm text-gray-500 mt-1">{error}</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <div className="text-center text-gray-500">
          <Wheat className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No feed data available</p>
        </div>
      </div>
    );
  }

  // Calculate statistics
  const totalAvailable = data.reduce((sum, item) => sum + item.available, 0);
  const totalConsumed = data.reduce((sum, item) => sum + item.consumed, 0);
  const utilizationRate = ((totalConsumed / totalAvailable) * 100).toFixed(1);

  return (
    <div className="w-full">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-800 flex items-center">
          <Wheat className="w-5 h-5 mr-2 text-amber-600" />
          Feed Consumption vs Availability
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Tracking feed inventory and consumption patterns
        </p>
      </div>

      {/* Statistics Summary */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="bg-amber-50 p-3 rounded-lg">
          <p className="text-xs text-gray-600">Total Available</p>
          <p className="text-lg font-bold text-amber-600">{totalAvailable.toLocaleString()} kg</p>
        </div>
        <div className="bg-yellow-50 p-3 rounded-lg">
          <p className="text-xs text-gray-600">Total Consumed</p>
          <p className="text-lg font-bold text-yellow-600">{totalConsumed.toLocaleString()} kg</p>
        </div>
        <div className="bg-green-50 p-3 rounded-lg">
          <p className="text-xs text-gray-600">Utilization</p>
          <p className="text-lg font-bold text-green-600">{utilizationRate}%</p>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={height}>
        <AreaChart
          data={data}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <defs>
            <linearGradient id="colorAvailable" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.8} />
              <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.1} />
            </linearGradient>
            <linearGradient id="colorConsumed" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#eab308" stopOpacity={0.8} />
              <stop offset="95%" stopColor="#eab308" stopOpacity={0.1} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAxis
            dataKey="date"
            stroke="#666"
            tick={{ fontSize: 12 }}
            tickMargin={10}
          />
          <YAxis
            stroke="#666"
            tick={{ fontSize: 12 }}
            label={{ value: 'Feed (kg)', angle: -90, position: 'insideLeft' }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{ paddingTop: '20px' }}
            iconType="rect"
          />
          <Area
            type="monotone"
            dataKey="available"
            name="Available Feed (kg)"
            stroke="#f59e0b"
            strokeWidth={2}
            fillOpacity={1}
            fill="url(#colorAvailable)"
            animationDuration={1000}
          />
          <Area
            type="monotone"
            dataKey="consumed"
            name="Consumed Feed (kg)"
            stroke="#eab308"
            strokeWidth={2}
            fillOpacity={1}
            fill="url(#colorConsumed)"
            animationDuration={1000}
          />
        </AreaChart>
      </ResponsiveContainer>

      {/* Efficiency Indicator */}
      <div className="mt-4 bg-gradient-to-r from-amber-50 to-yellow-50 p-3 rounded-lg">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-700">Feed Efficiency</span>
          <div className="flex items-center">
            <div className="w-32 h-2 bg-gray-200 rounded-full mr-2">
              <div
                className="h-full bg-gradient-to-r from-amber-500 to-yellow-500 rounded-full"
                style={{ width: `${Math.min(parseFloat(utilizationRate), 100)}%` }}
              ></div>
            </div>
            <span className="text-sm font-bold text-gray-700">{utilizationRate}%</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FoodHistoryChart;
